import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  BanknotesIcon,
  CreditCardIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  PlusIcon,
  MinusIcon,
  CurrencyDollarIcon,
  BuildingStorefrontIcon,
  ShoppingBagIcon,
  ArrowRightOnRectangleIcon,
  ArrowDownOnSquareIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { walletsAPI } from '../../config/api';
import LoadingScreen from '../../components/common/LoadingScreen';
import toast from 'react-hot-toast';

const LabWallet = () => {
  const { t } = useTranslation();
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [withdrawalForm, setWithdrawalForm] = useState({
    amount: '',
    bankName: '',
    accountName: '',
    iban: '',
    notes: ''
  });

  // Fetch wallet data
  const fetchWalletData = async () => {
    try {
      setLoading(true);
      console.log('🔄 جاري جلب بيانات المحفظة...');
      const response = await walletsAPI.getMyWallet();
      console.log('📊 استجابة API المحفظة:', response);
      
      // الاستجابة تأتي في response.data بسبب axios interceptor
      if (response.data && response.data.success) {
        setWalletData(response.data.data);
        console.log('✅ تم تحديث بيانات المحفظة:', response.data.data);
      } else {
        console.error('❌ فشل في جلب بيانات المحفظة:', response);
        toast.error(response.data?.message || 'حدث خطأ في جلب بيانات المحفظة');
      }
    } catch (error) {
      console.error('❌ خطأ في جلب بيانات المحفظة:', error);
      toast.error(error.response?.data?.message || 'حدث خطأ في جلب بيانات المحفظة');
    } finally {
      setLoading(false);
    }
  };

  // Fetch transactions
  const fetchTransactions = async (page = 1) => {
    try {
      setTransactionsLoading(true);
      const params = {
        page,
        limit: 10,
        search: searchTerm || undefined,
        type: filterType !== 'all' ? filterType : undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };

      console.log('🔄 جاري جلب المعاملات مع المعاملات:', params);
      const response = await walletsAPI.getTransactions(params);
      console.log('📋 استجابة API المعاملات:', response);
      
      // الاستجابة تأتي في response.data بسبب axios interceptor
      if (response.data && response.data.success) {
        setTransactions(response.data.data.transactions || []);
        setPagination(response.data.data.pagination || {});
        console.log('✅ تم تحديث المعاملات:', response.data.data.transactions?.length || 0, 'معاملة');
      } else {
        console.error('❌ فشل في جلب المعاملات:', response);
        toast.error(response.data?.message || 'حدث خطأ في جلب المعاملات');
      }
    } catch (error) {
      console.error('❌ خطأ في جلب المعاملات:', error);
      toast.error(error.response?.data?.message || 'حدث خطأ في جلب المعاملات');
    } finally {
      setTransactionsLoading(false);
    }
  };

  // Fetch withdrawal requests
  const fetchWithdrawalRequests = async () => {
    try {
      const response = await walletsAPI.getMyWithdrawalRequests();
      if (response.data && response.data.success) {
        setWithdrawalRequests(response.data.data.requests || []);
      }
    } catch (error) {
      console.error('❌ خطأ في جلب طلبات السحب:', error);
    }
  };

  useEffect(() => {
    console.log('🚀 تحميل صفحة المحفظة...');
    fetchWalletData();
    fetchWithdrawalRequests();
  }, []);

  useEffect(() => {
    console.log('🔄 تحديث المعاملات - الصفحة:', currentPage, 'النوع:', filterType, 'الحالة:', filterStatus);
    fetchTransactions(currentPage);
  }, [currentPage, filterType, filterStatus]);

  // Search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        fetchTransactions(1);
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Get transaction icon
  const getTransactionIcon = (type, category) => {
    switch (type) {
      case 'credit':
        if (category === 'sale') return ArrowDownOnSquareIcon;
        if (category === 'deposit') return PlusIcon;
        return ArrowTrendingUpIcon;
      case 'debit':
        if (category === 'commission') return BuildingStorefrontIcon;
        if (category === 'withdrawal') return ArrowRightOnRectangleIcon;
        return ArrowTrendingDownIcon;
      default:
        return ArrowPathIcon;
    }
  };

  // Get transaction color
  const getTransactionColor = (type, status) => {
    if (status === 'failed') return 'text-red-600 bg-red-100';
    if (status === 'pending') return 'text-yellow-600 bg-yellow-100';
    
    switch (type) {
      case 'credit':
        return 'text-green-600 bg-green-100';
      case 'debit':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-blue-600 bg-blue-100';
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      completed: { text: 'مكتمل', color: 'bg-green-100 text-green-700' },
      frozen: { text: 'مجمد', color: 'bg-amber-100 text-amber-700' },
      pending: { text: 'قيد المعالجة', color: 'bg-yellow-100 text-yellow-700' },
      failed: { text: 'فاشل', color: 'bg-red-100 text-red-700' },
      refunded: { text: 'مُسترد', color: 'bg-gray-100 text-gray-700' }
    };
    return statusMap[status] || { text: 'غير محدد', color: 'bg-gray-100 text-gray-700' };
  };

  // Get transaction type text
  const getTransactionTypeText = (type, category) => {
    if (type === 'credit') {
      switch (category) {
        case 'sale': return 'دفع من طلب';
        case 'deposit': return 'إيداع خارجي';
        case 'refund': return 'استرداد';
        default: return 'إيداع';
      }
    } else if (type === 'debit') {
      switch (category) {
        case 'commission': return 'عمولة المنصة';
        case 'withdrawal': return 'سحب';
        case 'purchase': return 'شراء';
        default: return 'خصم';
      }
    }
    return 'معاملة';
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle withdrawal request
  const handleWithdrawalRequest = async () => {
    try {
      // Validation
      if (!withdrawalForm.amount || withdrawalForm.amount < 100) {
        toast.error(t('labWallet.withdrawalForm.minimumAmount'));
        return;
      }

      if (withdrawalForm.amount > (walletData?.availableBalance || 0)) {
        toast.error(t('labWallet.withdrawalForm.exceedsBalance'));
        return;
      }

      if (!withdrawalForm.bankName || !withdrawalForm.accountName || !withdrawalForm.iban) {
        toast.error(t('labWallet.withdrawalForm.bankDetailsRequired'));
        return;
      }

      if (!/^SA\d{22}$/.test(withdrawalForm.iban.replace(/\s/g, ''))) {
        toast.error(t('labWallet.withdrawalForm.invalidIban'));
        return;
      }

      const requestData = {
        amount: parseFloat(withdrawalForm.amount),
        bankAccount: {
          bankName: withdrawalForm.bankName,
          accountName: withdrawalForm.accountName,
          iban: withdrawalForm.iban.replace(/\s/g, '').toUpperCase()
        },
        notes: withdrawalForm.notes
      };

      await walletsAPI.requestWithdrawal(requestData);
      
      toast.success(t('labWallet.withdrawalForm.withdrawalSuccess'));
      setShowWithdrawalModal(false);
      setWithdrawalForm({
        amount: '',
        bankName: '',
        accountName: '',
        iban: '',
        notes: ''
      });
      
      // Refresh data
      fetchWithdrawalRequests();
      fetchWalletData();
      
    } catch (error) {
      toast.error(error.response?.data?.message || t('labWallet.withdrawalForm.withdrawalError'));
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 300
      }
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  // Debug info
  console.log('🔍 حالة البيانات الحالية:', {
    walletData,
    transactions,
    transactionsLength: transactions?.length,
    loading,
    transactionsLoading
  });

  return (
    <div className="w-full max-w-full overflow-hidden">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
        <div className="px-6 py-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BanknotesIcon className="h-8 w-8 ml-3 text-brand-blue" />
            {t('labWallet.wallet')}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('labWallet.walletManagement')}
          </p>
        </div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-6 md:space-y-8 px-2 md:px-0"
      >
        {/* Wallet Balance Cards */}
        <motion.div variants={itemVariants}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Total Balance */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">{t('labWallet.totalBalance')}</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {walletData?.balance !== undefined ? 
                      walletData.balance.toLocaleString('ar-SA') : 
                      '0'
                    } <span className="text-lg">{t('labWallet.riyal')}</span>
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-xl">
                  <BanknotesIcon className="h-8 w-8 text-blue-600" />
                </div>
              </div>
            </div>

            {/* Available Balance */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">{t('labWallet.availableBalance')}</p>
                  <p className="text-3xl font-bold text-green-600">
                    {walletData?.availableBalance !== undefined ? 
                      walletData.availableBalance.toLocaleString('ar-SA') : 
                      '0'
                    } <span className="text-lg">{t('labWallet.riyal')}</span>
                  </p>
                  <p className="text-green-500 text-xs">{t('labWallet.availableForWithdrawal')}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-xl">
                  <CheckCircleIcon className="h-8 w-8 text-green-600" />
                </div>
              </div>
            </div>

            {/* Frozen Balance */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">{t('labWallet.frozenBalance')}</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {walletData?.frozenAmount !== undefined ? 
                      walletData.frozenAmount.toLocaleString('ar-SA') : 
                      '0'
                    } <span className="text-lg">{t('labWallet.riyal')}</span>
                  </p>
                  <p className="text-amber-500 text-xs">{t('labWallet.frozenUntilDelivery')}</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-xl">
                  <ClockIcon className="h-8 w-8 text-amber-600" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Withdrawal Section */}
        <motion.div variants={itemVariants}>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-4 md:px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <ArrowRightOnRectangleIcon className="h-5 w-5 ml-2 text-blue-600" />
                  طلبات السحب
                </h3>
                <button
                  onClick={() => setShowWithdrawalModal(true)}
                  disabled={!walletData || walletData.availableBalance < 100}
                  className="px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm"
                >
                  <PlusIcon className="h-4 w-4 ml-1" />
                  طلب سحب جديد
                </button>
              </div>
            </div>

            <div className="p-4 md:p-6">
              {withdrawalRequests.length === 0 ? (
                <div className="text-center py-8">
                  <ArrowRightOnRectangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">لا توجد طلبات سحب</h4>
                  <p className="text-gray-500 mb-4">يمكنك طلب سحب الأموال المتاحة</p>
                  {walletData && walletData.availableBalance >= 100 && (
                    <button
                      onClick={() => setShowWithdrawalModal(true)}
                      className="px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-colors"
                    >
                      طلب سحب جديد
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {withdrawalRequests.map((request) => (
                    <div key={request._id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">{request.requestNumber}</h4>
                          <p className="text-sm text-gray-500">{formatDate(request.createdAt)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-blue-600">{request.amount.toLocaleString('ar-SA')} ريال</p>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            request.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            request.status === 'processed' ? 'bg-green-100 text-green-700' :
                            request.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {request.status === 'pending' ? 'قيد المراجعة' :
                             request.status === 'processed' ? 'تم المعالجة' :
                             request.status === 'rejected' ? 'مرفوض' : request.status}
                          </span>
                        </div>
                      </div>
                      
                      <div className="text-sm text-gray-600">
                        <p>البنك: {request.bankAccount.bankName}</p>
                        <p>الحساب: {request.bankAccount.accountName}</p>
                        {request.rejectionReason && (
                          <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                            <p className="text-red-700 font-medium">سبب الرفض:</p>
                            <p className="text-red-600">{request.rejectionReason}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Recent Activity Summary */}
        <motion.div variants={itemVariants}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">إجمالي الإيداعات</p>
                  <p className="text-2xl font-bold text-green-600">
                    {transactions
                      .filter(t => t.type === 'credit' && ['completed', 'frozen'].includes(t.status))
                      .reduce((sum, t) => sum + t.amount, 0)
                      .toLocaleString('ar-SA')} ريال
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-xl">
                  <ArrowTrendingUpIcon className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">إجمالي المسحوبات</p>
                  <p className="text-2xl font-bold text-red-600">
                    {transactions
                      .filter(t => t.type === 'debit' && t.status === 'completed')
                      .reduce((sum, t) => sum + t.amount, 0)
                      .toLocaleString('ar-SA')} ريال
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-xl">
                  <ArrowTrendingDownIcon className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">آخر معاملة</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {transactions.length > 0 ? 
                      `${transactions[0].amount.toLocaleString('ar-SA')} ريال` : 
                      'لا توجد معاملات'
                    }
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-xl">
                  <ClockIcon className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Transactions Section */}
        <motion.div variants={itemVariants}>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Transactions Header */}
            <div className="px-4 md:px-6 py-4 border-b border-gray-200">
              <div className="flex flex-col gap-4">
                <h3 className="text-lg font-semibold text-gray-900">سجل المعاملات</h3>
                
                {/* Search and Filters */}
                <div className="flex flex-col gap-3">
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="البحث في المعاملات..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent text-sm"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent text-sm"
                    >
                      <option value="all">جميع الأنواع</option>
                      <option value="credit">إيداعات</option>
                      <option value="debit">مسحوبات</option>
                    </select>
                    
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent text-sm"
                    >
                      <option value="all">جميع الحالات</option>
                      <option value="completed">مكتمل</option>
                      <option value="frozen">مجمد</option>
                      <option value="pending">قيد المعالجة</option>
                      <option value="failed">فاشل</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Transactions List */}
            <div className="divide-y divide-gray-200">
              {transactionsLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue mx-auto"></div>
                  <p className="text-gray-500 mt-2">جارٍ تحميل المعاملات...</p>
                </div>
              ) : !transactions || transactions.length === 0 ? (
                <div className="p-8 text-center">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد معاملات</h3>
                  <p className="text-gray-500">
                    {!transactions ? 'لم يتم تحميل المعاملات بعد' : 'لم يتم العثور على أي معاملات مالية'}
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    تحقق من وحدة التحكم للمزيد من التفاصيل
                  </p>
                </div>
              ) : (
                transactions.map((transaction, index) => {
                  const TransactionIcon = getTransactionIcon(transaction.type, transaction.category);
                  const statusBadge = getStatusBadge(transaction.status);
                  
                  return (
                    <motion.div
                      key={transaction._id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 md:p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedTransaction(transaction);
                        setShowTransactionModal(true);
                      }}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 overflow-hidden">
                        {/* Mobile Layout */}
                        <div className="flex items-start space-x-3 space-x-reverse flex-1 min-w-0 overflow-hidden">
                          <div className={`p-2 md:p-3 rounded-lg md:rounded-xl ${getTransactionColor(transaction.type, transaction.status)}`}>
                            <TransactionIcon className="h-5 w-5 md:h-6 md:w-6" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <h4 className="text-sm font-medium text-gray-900 truncate flex-1">
                                {getTransactionTypeText(transaction.type, transaction.category)}
                              </h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge.color} flex-shrink-0`}>
                                {statusBadge.text}
                              </span>
                            </div>
                            
                            <p className="text-sm text-gray-500 break-words overflow-hidden">
                              <span className="block overflow-hidden text-ellipsis whitespace-nowrap md:whitespace-normal">
                                {transaction.description.length > 60 ? 
                                  `${transaction.description.substring(0, 60)}...` : 
                                  transaction.description}
                              </span>
                            </p>
                            
                            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-4 mt-2">
                              <p className="text-xs text-gray-400">
                                {formatDate(transaction.createdAt)}
                              </p>
                              {transaction.reference && (
                                <p className="text-xs text-gray-400 truncate max-w-32 md:max-w-none">
                                  {transaction.reference}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Amount - Always on the right */}
                        <div className="text-right flex-shrink-0 min-w-0">
                          <p className={`text-base md:text-lg font-bold ${
                            transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                          } truncate`}>
                            {transaction.type === 'credit' ? '+' : '-'}
                            {transaction.amount.toLocaleString('ar-SA')} ريال
                          </p>
                          {transaction.fees && transaction.fees.totalFees > 0 && (
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              رسوم: {transaction.fees.totalFees.toLocaleString('ar-SA')} ريال
                            </p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-700">
                    عرض {((currentPage - 1) * pagination.limit) + 1} إلى{' '}
                    {Math.min(currentPage * pagination.limit, pagination.total)} من{' '}
                    {pagination.total} معاملة
                  </p>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      السابق
                    </button>
                    
                    <span className="px-3 py-1 text-sm bg-brand-blue text-white rounded-md">
                      {currentPage}
                    </span>
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                      disabled={currentPage === pagination.totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      التالي
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* Transaction Details Modal */}
      {showTransactionModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">تفاصيل المعاملة</h3>
                <button
                  onClick={() => setShowTransactionModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XCircleIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                  <div className={`p-4 rounded-xl ${getTransactionColor(selectedTransaction.type, selectedTransaction.status)}`}>
                    {React.createElement(getTransactionIcon(selectedTransaction.type, selectedTransaction.category), {
                      className: "h-8 w-8"
                    })}
                  </div>
                </div>

                <div className="text-center">
                  <p className={`text-2xl font-bold ${
                    selectedTransaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {selectedTransaction.type === 'credit' ? '+' : '-'}
                    {selectedTransaction.amount.toLocaleString('ar-SA')} ريال
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {getTransactionTypeText(selectedTransaction.type, selectedTransaction.category)}
                  </p>
                </div>

                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">الحالة:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(selectedTransaction.status).color}`}>
                      {getStatusBadge(selectedTransaction.status).text}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">التاريخ:</span>
                    <span className="text-sm text-gray-900">{formatDate(selectedTransaction.createdAt)}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">رقم المعاملة:</span>
                    <span className="text-sm text-gray-900 font-mono">{selectedTransaction.transactionId}</span>
                  </div>

                  {selectedTransaction.reference && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">المرجع:</span>
                      <span className="text-sm text-gray-900">{selectedTransaction.reference}</span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">الوصف:</span>
                    <span className="text-sm text-gray-900 text-left">{selectedTransaction.description}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">الرصيد قبل:</span>
                    <span className="text-sm text-gray-900">{selectedTransaction.balanceBefore?.toLocaleString('ar-SA')} ريال</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">الرصيد بعد:</span>
                    <span className="text-sm text-gray-900">{selectedTransaction.balanceAfter?.toLocaleString('ar-SA')} ريال</span>
                  </div>

                  {selectedTransaction.fees && selectedTransaction.fees.totalFees > 0 && (
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">إجمالي الرسوم:</span>
                        <span className="text-sm text-red-600">{selectedTransaction.fees.totalFees.toLocaleString('ar-SA')} ريال</span>
                      </div>
                      {selectedTransaction.fees.platformFee > 0 && (
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-gray-500">عمولة المنصة:</span>
                          <span className="text-xs text-gray-700">{selectedTransaction.fees.platformFee.toLocaleString('ar-SA')} ريال</span>
                        </div>
                      )}
                      {selectedTransaction.fees.paymentFee > 0 && (
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-gray-500">رسوم الدفع:</span>
                          <span className="text-xs text-gray-700">{selectedTransaction.fees.paymentFee.toLocaleString('ar-SA')} ريال</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Withdrawal Request Modal */}
      {showWithdrawalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">طلب سحب جديد</h3>
                <button
                  onClick={() => setShowWithdrawalModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XCircleIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Available Balance Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 ml-2" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">الرصيد المتاح للسحب</p>
                      <p className="text-lg font-bold text-blue-600">
                        {walletData?.availableBalance?.toLocaleString('ar-SA')} ريال
                      </p>
                    </div>
                  </div>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    المبلغ المطلوب سحبه <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="100"
                    max={walletData?.availableBalance || 0}
                    value={withdrawalForm.amount}
                    onChange={(e) => setWithdrawalForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="أدخل المبلغ (الحد الأدنى 100 ريال)"
                  />
                </div>

                {/* Bank Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم البنك <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={withdrawalForm.bankName}
                    onChange={(e) => setWithdrawalForm(prev => ({ ...prev, bankName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">اختر البنك</option>
                    <option value="البنك الأهلي السعودي">البنك الأهلي السعودي</option>
                    <option value="بنك الراجحي">بنك الراجحي</option>
                    <option value="بنك الرياض">بنك الرياض</option>
                    <option value="البنك السعودي الفرنسي">البنك السعودي الفرنسي</option>
                    <option value="بنك ساب">بنك ساب</option>
                    <option value="البنك السعودي للاستثمار">البنك السعودي للاستثمار</option>
                    <option value="بنك الجزيرة">بنك الجزيرة</option>
                    <option value="البنك الأول">البنك الأول</option>
                    <option value="بنك البلاد">بنك البلاد</option>
                    <option value="بنك الإنماء">بنك الإنماء</option>
                  </select>
                </div>

                {/* Account Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم صاحب الحساب <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={withdrawalForm.accountName}
                    onChange={(e) => setWithdrawalForm(prev => ({ ...prev, accountName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="أدخل اسم صاحب الحساب"
                  />
                </div>

                {/* IBAN */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    رقم الآيبان <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={withdrawalForm.iban}
                    onChange={(e) => setWithdrawalForm(prev => ({ ...prev, iban: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="SA0000000000000000000000"
                    maxLength="24"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ملاحظات (اختياري)
                  </label>
                  <textarea
                    value={withdrawalForm.notes}
                    onChange={(e) => setWithdrawalForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder="أضف أي ملاحظات..."
                    maxLength="500"
                  />
                </div>

                {/* Warning */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 ml-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-amber-800 font-medium">تنبيه مهم:</p>
                      <p className="text-amber-700 mt-1">
                        • سيتم مراجعة طلب السحب من قبل الإدارة
                        <br />
                        • مدة المعالجة: 2-5 أيام عمل
                        <br />
                        • تأكد من صحة بيانات الحساب البنكي
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowWithdrawalModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleWithdrawalRequest}
                  className="px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-colors flex items-center"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4 ml-2" />
                  إرسال الطلب
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LabWallet;